import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Alert,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Add as AddIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  addChatMessage,
  setChatLoading,
  setChatError,
  setBookingFlow,
  setBookings,
  setBookingsLoading,
  setBookingsError,
  addBooking,
} from '../../store/slices/autosphereSlice';
import { autosphereApi } from '../../store/api/autosphereApi';
import { ChatMessage, BookingCreate } from '../../types';
import ErrorAlert from '../../components/common/ErrorAlert';
import FormattedText from '../../components/common/FormattedText';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { format } from 'date-fns';

const AutoSphere: React.FC = () => {
  const dispatch = useAppDispatch();
  const { chat, bookings } = useAppSelector((state) => state.autosphere);
  const [activeTab, setActiveTab] = useState(0);
  
  // Chat state
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [thinkingDots, setThinkingDots] = useState('');

  // Booking creation state
  const [formData, setFormData] = useState<BookingCreate>({
    booking_type: 'Service',
    name: '',
    phone: '',
    vehicle_model: '',
    preferred_date: undefined,
    natural_language: '',
  });
  const [successMessage, setSuccessMessage] = useState('');

  // Search state
  const [searchParams, setSearchParams] = useState({
    booking_id: '',
    phone: '',
    booking_type: '',
  });

  // Chat effects
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages, chat.loading]);

  useEffect(() => {
    if (!chat.loading) {
      setThinkingDots('');
      return;
    }

    let dotCount = 0;
    const interval = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      setThinkingDots('.'.repeat(dotCount));
    }, 500);

    return () => clearInterval(interval);
  }, [chat.loading]);

  useEffect(() => {
    if (!chat.loading && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [chat.loading]);

  useEffect(() => {
    if (inputRef.current && activeTab === 0) {
      inputRef.current.focus();
    }
  }, [activeTab]);

  // Chat handlers
  const handleSend = async () => {
    if (!message.trim() || chat.loading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
    };

    dispatch(addChatMessage(userMessage));
    setMessage('');
    dispatch(setChatLoading(true));
    dispatch(setChatError(null));

    try {
      const chatHistory = chat.messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await autosphereApi.chat({
        message: userMessage.content,
        chat_history: chatHistory,
      });

      if (response.success && response.data) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.data.response,
        };

        dispatch(addChatMessage(assistantMessage));
        dispatch(setBookingFlow(response.data.booking_flow || false));
      } else {
        dispatch(setChatError(response.message || 'Failed to get response'));
      }
    } catch (error: any) {
      dispatch(
        setChatError(
          error.response?.data?.message || error.message || 'Failed to get response'
        )
      );
    } finally {
      dispatch(setChatLoading(false));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Booking handlers
  const handleFormChange = (field: keyof BookingCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateBooking = async () => {
    if (!formData.name.trim() || !formData.phone.trim() || !formData.vehicle_model.trim()) {
      dispatch(setBookingsError('Name, phone, and vehicle model are required'));
      return;
    }

    dispatch(setBookingsLoading(true));
    setSuccessMessage('');

    try {
      const bookingData: BookingCreate = {
        booking_type: formData.booking_type,
        name: formData.name,
        phone: formData.phone,
        vehicle_model: formData.vehicle_model,
        preferred_date: formData.preferred_date,
        natural_language: formData.natural_language || undefined,
      };

      const response = await autosphereApi.createBooking(bookingData);

      if (response.success && response.data) {
        dispatch(addBooking(response.data));
        setSuccessMessage(response.message || 'Booking created successfully!');
        setFormData({
          booking_type: 'Service',
          name: '',
          phone: '',
          vehicle_model: '',
          preferred_date: undefined,
          natural_language: '',
        });
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        dispatch(setBookingsError(response.message || 'Failed to create booking'));
      }
    } catch (error: any) {
      dispatch(
        setBookingsError(
          error.response?.data?.message || error.message || 'Failed to create booking'
        )
      );
    } finally {
      dispatch(setBookingsLoading(false));
    }
  };

  // Search handlers
  const handleSearch = async () => {
    dispatch(setBookingsLoading(true));

    try {
      const params: any = {};
      if (searchParams.booking_id) params.booking_id = searchParams.booking_id;
      if (searchParams.phone) params.phone = searchParams.phone;
      if (searchParams.booking_type) params.booking_type = searchParams.booking_type;

      const response = await autosphereApi.searchBookings(params);

      if (response.success && response.data) {
        dispatch(setBookings(response.data));
      } else {
        dispatch(setBookingsError(response.message || 'Search failed'));
      }
    } catch (error: any) {
      dispatch(
        setBookingsError(
          error.response?.data?.message || error.message || 'Search failed'
        )
      );
    } finally {
      dispatch(setBookingsLoading(false));
    }
  };

  return (
    <Box>
      {/* Header Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #ff6b9d 0%, #ffa07a 100%)',
          borderRadius: 2,
          p: 4,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CarIcon sx={{ fontSize: 48, color: 'white' }} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            AutoSphere Motors AI Assistant
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Your Intelligent Automotive Assistant • Book Services • Get Answers
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="AI Assistant" icon={<BotIcon />} iconPosition="start" />
        <Tab label="Book a Service / Test Drive" icon={<AddIcon />} iconPosition="start" />
        <Tab label="Search Booking" icon={<SearchIcon />} iconPosition="start" />
      </Tabs>

      {/* Tab 0: AI Assistant */}
      {activeTab === 0 && (
        <Card sx={{ height: { xs: 'calc(100vh - 400px)', md: 'calc(100vh - 450px)' }, display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1, overflow: 'auto', pb: 2 }}>
            {chat.messages.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                }}
              >
                <BotIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Start a conversation with AutoSphere AI
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Ask about our services, book appointments, or get help with your vehicle
                </Typography>
              </Box>
            )}

            {chat.messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    gap: 1,
                    maxWidth: '70%',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
                  </Avatar>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <FormattedText text={msg.content} />
                    ) : (
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}

            {chat.loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, maxWidth: '70%' }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                    <BotIcon fontSize="small" />
                  </Avatar>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      Thinking{thinkingDots}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </CardContent>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {chat.error && (
              <ErrorAlert
                message={chat.error}
                onClose={() => dispatch(setChatError(null))}
              />
            )}
            {chat.bookingFlow && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Booking flow is active. You can create a booking or continue chatting.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={chat.loading}
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={chat.loading || !message.trim()}
                startIcon={chat.loading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                Send
              </Button>
            </Box>
          </Box>
        </Card>
      )}

      {/* Tab 1: Book a Service / Test Drive */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Book a Service / Test Drive
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Booking Type"
                  value={formData.booking_type}
                  onChange={(e) => handleFormChange('booking_type', e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Test Drive">Test Drive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vehicle Model"
                  value={formData.vehicle_model}
                  onChange={(e) => handleFormChange('vehicle_model', e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Preferred Date"
                  value={formData.preferred_date || ''}
                  onChange={(e) => handleFormChange('preferred_date', e.target.value || undefined)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Natural Language (Optional)"
                  value={formData.natural_language}
                  onChange={(e) => handleFormChange('natural_language', e.target.value)}
                  placeholder="e.g., I want to book a service for my Toyota Camry on December 25th"
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            {bookings.error && (
              <ErrorAlert
                message={bookings.error}
                onClose={() => dispatch(setBookingsError(null))}
              />
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBooking}
              disabled={bookings.loading}
              sx={{ mt: 2 }}
            >
              {bookings.loading ? 'Creating...' : 'Create Booking'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Search Booking */}
      {activeTab === 2 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon />
                Search Booking
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Booking ID"
                    value={searchParams.booking_id}
                    onChange={(e) =>
                      setSearchParams((prev) => ({ ...prev, booking_id: e.target.value }))
                    }
                    placeholder="e.g., AS-20241225-1234"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={searchParams.phone}
                    onChange={(e) =>
                      setSearchParams((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Booking Type (optional)"
                    value={searchParams.booking_type}
                    onChange={(e) =>
                      setSearchParams((prev) => ({ ...prev, booking_type: e.target.value }))
                    }
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Service">Service</MenuItem>
                    <MenuItem value="Test Drive">Test Drive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={bookings.loading}
                sx={{ mt: 2 }}
              >
                {bookings.loading ? 'Searching...' : 'Search'}
              </Button>
            </CardContent>
          </Card>

          {bookings.loading && <LoadingSpinner />}

          {bookings.error && (
            <ErrorAlert
              message={bookings.error}
              onClose={() => dispatch(setBookingsError(null))}
            />
          )}

          {bookings.list.length > 0 && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Booking Found!
              </Alert>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Vehicle Model</TableCell>
                      <TableCell>Preferred Date</TableCell>
                      <TableCell>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.list.map((booking) => (
                      <TableRow key={booking.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {booking.booking_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.booking_type}
                            color={booking.booking_type === 'Service' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{booking.name}</TableCell>
                        <TableCell>{booking.phone}</TableCell>
                        <TableCell>{booking.vehicle_model}</TableCell>
                        <TableCell>
                          {booking.preferred_date
                            ? format(new Date(booking.preferred_date), 'MMM dd, yyyy')
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {format(new Date(booking.created_at), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {bookings.list.length === 0 && !bookings.loading && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No bookings found. Use the search form above to find bookings.
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AutoSphere;
